/**
 * Barbearia: identidade visual, vitrine pública e — o mais importante — os
 * parâmetros de negócio.
 *
 * Todo campo de `tenantSettingsSchema` é editável em tela. Nenhum deles pode
 * virar variável de ambiente: duas barbearias no mesmo servidor têm regras
 * diferentes, e essa é a razão de o produto ser um SaaS.
 */

import { isValidTimezone, SLOT_STEP_OPTIONS } from '@barbearia/core';
import { z } from 'zod';
import { availableSlugSchema, hexColorSchema, phoneSchema } from './common.js';

export const themeModeSchema = z.enum(['LIGHT', 'DARK', 'SYSTEM']);
export type ThemeMode = z.infer<typeof themeModeSchema>;

/**
 * O que a vitrine mostra a quem ainda não entrou.
 *
 * Três estados e não dois interruptores: há barbearia que faz questão de
 * divulgar o que atende e não quer tabela de preço na internet, e "preço
 * visível com serviço escondido" é um estado que não quer dizer nada.
 */
export const publicCatalogSchema = z.enum(['HIDDEN', 'SERVICES', 'SERVICES_AND_PRICES']);
export type PublicCatalog = z.infer<typeof publicCatalogSchema>;

export const PUBLIC_CATALOG_LABELS: Record<PublicCatalog, string> = {
  HIDDEN: 'Não mostrar',
  SERVICES: 'Só os serviços',
  SERVICES_AND_PRICES: 'Serviços e preços',
};

export const tenantSettingsSchema = z.object({
  slotStepMinutes: z
    .number()
    .int()
    .refine(
      (value) => (SLOT_STEP_OPTIONS as readonly number[]).includes(value),
      `Passo da grade deve ser um destes: ${SLOT_STEP_OPTIONS.join(', ')} minutos`,
    ),
  fixedSlotGrid: z.boolean(),
  overlapToleranceMinutes: z
    .number()
    .int()
    .min(0, 'A tolerância não pode ser negativa')
    .max(60, 'Mais de uma hora de sobreposição não é encaixe, é remarcação'),
  minLeadTimeMinutes: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 7),
  minCancelNoticeMinutes: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 7),
  maxFutureDays: z.number().int().min(1).max(365),
  autoConfirmRequests: z.boolean(),
  allowClientChooseBarber: z.boolean(),
  requireNewClientApproval: z.boolean(),
  showNoShowCounter: z.boolean(),
  /**
   * Os dois do WhatsApp nascem desligados, e o padrão é o que o projeto já
   * pratica em todo o resto: **nada é apagado**. Nome e telefone ajudam o
   * barbeiro mesmo sem foto para desempatar, e quem quiser guardar menos liga o
   * interruptor sabendo o que perde.
   */
  deleteContactsWithoutPhoto: z.boolean(),
  deleteContactsOnDisconnect: z.boolean(),
  publicCatalog: publicCatalogSchema,
  reminderLeadMinutes: z
    .number()
    .int()
    .min(0)
    .max(60 * 24 * 7),
});

export type TenantSettingsInput = z.infer<typeof tenantSettingsSchema>;

export const updateTenantSettingsSchema = tenantSettingsSchema.partial();

/** Modelos de mensagem do WhatsApp, com variáveis entre chaves duplas. */
export const messageTemplatesSchema = z.object({
  confirmation: z.string().max(1000).optional(),
  reminder: z.string().max(1000).optional(),
  cancellation: z.string().max(1000).optional(),
  passwordReset: z.string().max(1000).optional(),
  /** O corte anterior rendeu: dá para adiantar o cliente. */
  readyNow: z.string().max(1000).optional(),
  /** O corte anterior estourou: melhor avisar do que deixar esperando. */
  runningLate: z.string().max(1000).optional(),
  /** Vagou um horário para quem estava na lista de espera. */
  waitlistOpening: z.string().max(1000).optional(),
});

export type MessageTemplates = z.infer<typeof messageTemplatesSchema>;

/** Mensagens que o barbeiro dispara com o cliente já agendado. */
export const APPOINTMENT_MESSAGE_KINDS = [
  'confirmation',
  'reminder',
  'readyNow',
  'runningLate',
  'cancellation',
] as const;

export type AppointmentMessageKind = (typeof APPOINTMENT_MESSAGE_KINDS)[number];

export const MESSAGE_KIND_LABELS: Record<AppointmentMessageKind, string> = {
  confirmation: 'Mandar confirmação',
  reminder: 'Lembrar do horário',
  readyNow: 'Já pode vir',
  runningLate: 'Vou atrasar',
  cancellation: 'Avisar cancelamento',
};

export const DEFAULT_MESSAGE_TEMPLATES: Required<MessageTemplates> = {
  confirmation:
    'Oi {{cliente}}! Seu horário na {{barbearia}} está confirmado para {{data}} às {{hora}}. Até lá!',
  reminder: 'Oi {{cliente}}, passando pra lembrar do seu horário amanhã, {{data}} às {{hora}}.',
  cancellation:
    'Oi {{cliente}}, precisei cancelar seu horário de {{data}} às {{hora}}. Me chama pra remarcar?',
  passwordReset:
    'Oi {{cliente}}, use este link pra criar uma nova senha na {{barbearia}}: {{link}} (vale por {{validade}}).',
  // Duas mensagens que o barbeiro manda no meio do expediente, com a mão suja
  // de talco. Precisam sair em um toque, sem ele digitar nada.
  readyNow:
    'Oi {{cliente}}, adiantou aqui! Se puder vir agora, já te atendo. Seu horário era {{hora}}.',
  runningLate:
    'Oi {{cliente}}, o corte antes do seu passou do tempo. Vou atrasar uns {{minutos}} minutos no seu horário das {{hora}} — pode vir com calma.',
  // A mensagem que fecha o ciclo da lista de espera. Termina em pergunta de
  // propósito: a vaga não é reservada até a pessoa responder, e escrever
  // "está marcado" seria prometer o que o app não fez.
  waitlistOpening:
    'Oi {{cliente}}, vagou um horário na {{barbearia}}: {{data}} às {{hora}} para {{servico}}. Você tinha pedido pra avisar. Quer que eu guarde?',
};

/**
 * Quanto do rótulo o celular mostra na tela de início antes de cortar.
 *
 * Não é limite de gravação — é o número que a tela usa para avisar o dono de
 * que o nome dele não vai caber inteiro. iPhone e Android ficam por aí.
 */
export const HOME_SCREEN_LABEL_LIMIT = 12;

export const updateBrandingSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  /**
   * Vazio apaga: volta a valer o nome da barbearia. Por isso o `undefined`
   * (campo ausente no PATCH) e o `null` (campo enviado vazio) precisam
   * continuar sendo coisas diferentes daqui até o Prisma.
   */
  shortName: z
    .string()
    .trim()
    .max(30)
    .optional()
    .transform((value) => (value === undefined ? undefined : value || null)),
  themeMode: themeModeSchema.optional(),
  primaryColor: hexColorSchema.optional(),
  accentColor: hexColorSchema.optional(),
  phone: z.string().trim().max(30).optional(),
  address: z.string().trim().max(240).optional(),
  instagram: z.string().trim().max(60).optional(),
  about: z.string().trim().max(2000).optional(),
  timezone: z
    .string()
    .refine((value) => isValidTimezone(value), 'Fuso horário desconhecido')
    .optional(),
});

// ── Painel do dono do SaaS ──────────────────────────────────────────────────

export const tenantStatusSchema = z.enum(['TRIAL', 'ACTIVE', 'SUSPENDED']);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

export const TENANT_STATUS_LABELS: Record<TenantStatus, string> = {
  TRIAL: 'Em teste',
  ACTIVE: 'Ativa',
  SUSPENDED: 'Suspensa',
};

/**
 * Criação de uma barbearia pelo super-admin.
 *
 * O dono vem junto: uma barbearia sem ninguém que consiga entrar nela não
 * serve para nada, e criar os dois em transações separadas deixaria órfãos
 * quando a segunda falhasse.
 */
export const createTenantSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome da barbearia').max(120),
  slug: availableSlugSchema,
  timezone: z
    .string()
    .refine((value) => isValidTimezone(value), 'Fuso horário desconhecido')
    .default('America/Sao_Paulo'),
  plan: z.string().trim().max(40).default('free'),
  owner: z.object({
    name: z.string().trim().min(2, 'Informe o nome do dono').max(120),
    phone: phoneSchema,
    password: z.string().min(8, 'A senha precisa de pelo menos 8 caracteres').max(200),
  }),
});

export type CreateTenantInput = z.input<typeof createTenantSchema>;

export const updateTenantAdminSchema = z.object({
  status: tenantStatusSchema.optional(),
  plan: z.string().trim().max(40).optional(),
  maxStaff: z.number().int().min(1).max(200).nullable().optional(),
  validUntil: z.string().nullable().optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
});

export interface TenantAdminView {
  id: string;
  slug: string;
  name: string;
  status: TenantStatus;
  timezone: string;
  plan: string;
  validUntil: string | null;
  maxStaff: number | null;
  notes: string | null;
  createdAt: string;
  /** Endereço que a barbearia divulga. */
  url: string;
  counts: {
    staff: number;
    clients: number;
    services: number;
    /** Atendimentos nos últimos 30 dias — o sinal de que a conta está viva. */
    appointmentsLast30Days: number;
  };
  owner: {
    name: string;
    phone: string;
  } | null;
  lastAppointmentAt: string | null;
}

export interface AdminOverview {
  tenants: number;
  activeTenants: number;
  suspendedTenants: number;
  users: number;
  appointmentsLast30Days: number;
}

/** O que a página pública da barbearia mostra — sem exigir login. */
export interface TenantPublicView {
  slug: string;
  name: string;
  /** Nulo = o atalho na tela de início usa o nome inteiro. */
  shortName: string | null;
  timezone: string;
  themeMode: ThemeMode;
  primaryColor: string;
  accentColor: string;
  phone: string | null;
  address: string | null;
  instagram: string | null;
  about: string | null;
  logoUrl: string | null;
  iconUrl: string | null;
  /**
   * O que a barbearia escolheu mostrar. Vem junto com a lista porque a tela
   * precisa distinguir "escondeu o catálogo" de "ainda não cadastrou serviço" —
   * nos dois casos a lista chega vazia, e o texto a dizer é outro.
   */
  publicCatalog: PublicCatalog;
  /** Vazia quando o catálogo está oculto: preço nenhum sai daqui sem permissão. */
  services: Array<{
    id: string;
    name: string;
    description: string | null;
    /** Nulo quando a barbearia mostra os serviços mas não os preços. */
    priceCents: number | null;
    defaultDurationMinutes: number;
  }>;
  staff: Array<{
    id: string;
    name: string;
  }>;
}
